import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Download, ExternalLink, MapPin } from 'lucide-react';

// Menu PDFs organized by location
const MENU_PDFS = {
  main: {
    name: {
      en: 'El Tezal & Centro Los Cabos',
      es: 'El Tezal y Centro Los Cabos',
    },
    description: {
      en: 'Full Menu - June 2025',
      es: 'Menú Completo - Junio 2025',
    },
    pdfs: {
      en: '/assets/menus/menu-complete-en.pdf',
      es: '/assets/menus/menu-complete-es.pdf',
    },
  },
  carretera: {
    name: {
      en: 'Carretera Transpeninsular',
      es: 'Carretera Transpeninsular',
    },
    description: {
      en: 'Carreta Style Menu',
      es: 'Menú Estilo Carreta',
    },
    pdfs: {
      en: '/assets/menus/menu-carreta-en.pdf',
      es: '/assets/menus/menu-carreta-es.pdf',
    },
  },
};

export const MenuSection = () => {
  const { language, t } = useLanguage();
  const [selectedLocation, setSelectedLocation] = useState('main');
  const [menuLanguage, setMenuLanguage] = useState(language);
  const [isLoading, setIsLoading] = useState(true);

  const currentMenu = MENU_PDFS[selectedLocation];
  const currentPdf = currentMenu.pdfs[menuLanguage];

  const handleLocationChange = (location) => {
    if (location !== selectedLocation) {
      setIsLoading(true);
      setSelectedLocation(location);
    }
  };

  const handleLanguageSwitch = (lang) => {
    if (lang !== menuLanguage) {
      setIsLoading(true);
      setMenuLanguage(lang);
    }
  };

  return (
    <section id="menu" className="relative py-28 lg:py-36 bg-[#FAFAF8] overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#0D9488]/3 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#F0FDFA] rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-2xl mx-auto text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold text-[#0D9488] tracking-[0.2em] uppercase mb-4">
              {t('menu.tagline')}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#0C4A6E] mb-4">
              {t('menu.title')}
            </h2>
            <p className="text-lg text-[#64748B]">
              {t('menu.subtitle')}
            </p>
          </motion.div>
        </div>

        {/* Location Selector */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex justify-center mb-6"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-0 bg-white rounded-2xl sm:rounded-full p-2 shadow-lg border border-[#E2E8F0]">
            <button
              onClick={() => handleLocationChange('main')}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl sm:rounded-full text-sm font-semibold transition-all duration-300 w-full sm:w-auto justify-center ${
                selectedLocation === 'main'
                  ? 'bg-[#0C4A6E] text-white shadow-md'
                  : 'text-[#64748B] hover:text-[#0C4A6E] hover:bg-[#F1F5F9]'
              }`}
            >
              <MapPin className="w-4 h-4" />
              <span>{MENU_PDFS.main.name[language]}</span>
            </button>
            <div className="hidden sm:block w-px h-8 bg-[#E2E8F0] mx-2" />
            <button
              onClick={() => handleLocationChange('carretera')}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl sm:rounded-full text-sm font-semibold transition-all duration-300 w-full sm:w-auto justify-center ${
                selectedLocation === 'carretera'
                  ? 'bg-[#0C4A6E] text-white shadow-md'
                  : 'text-[#64748B] hover:text-[#0C4A6E] hover:bg-[#F1F5F9]'
              }`}
            >
              <MapPin className="w-4 h-4" />
              <span>{MENU_PDFS.carretera.name[language]}</span>
            </button>
          </div>
        </motion.div>

        {/* Language Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center bg-white rounded-full p-1.5 shadow-sm border border-[#E2E8F0]">
            <button
              onClick={() => handleLanguageSwitch('en')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                menuLanguage === 'en'
                  ? 'bg-[#0D9488] text-white shadow-md shadow-[#0D9488]/20'
                  : 'text-[#64748B] hover:text-[#0D9488]'
              }`}
            >
              English
            </button>
            <span className="w-px h-5 bg-[#E2E8F0] mx-1" />
            <button
              onClick={() => handleLanguageSwitch('es')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                menuLanguage === 'es'
                  ? 'bg-[#0D9488] text-white shadow-md shadow-[#0D9488]/20'
                  : 'text-[#64748B] hover:text-[#0D9488]'
              }`}
            >
              Español
            </button>
          </div>
        </motion.div>

        {/* PDF Menu Display */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${selectedLocation}-${menuLanguage}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="max-w-5xl mx-auto"
          >
            {/* PDF Embed Container */}
            <div className="relative bg-white rounded-3xl shadow-xl border border-[#E2E8F0] overflow-hidden">
              {/* PDF Header Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-6 py-4 bg-gradient-to-r from-[#0C4A6E] to-[#0D9488] text-white gap-3">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold block">
                      {currentMenu.name[language]}
                    </span>
                    <span className="text-white/80 text-sm">
                      {currentMenu.description[menuLanguage]}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <a
                    href={`https://docs.google.com/viewer?url=${encodeURIComponent(window.location.origin + currentPdf)}&embedded=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{menuLanguage === 'en' ? 'Open' : 'Abrir'}</span>
                  </a>
                  <a
                    href={currentPdf}
                    download
                    className="flex items-center justify-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none"
                  >
                    <Download className="w-4 h-4" />
                    <span>{menuLanguage === 'en' ? 'Download' : 'Descargar'}</span>
                  </a>
                </div>
              </div>

              {/* PDF Viewer */}
              <div className="relative w-full" style={{ height: '75vh', minHeight: '550px', maxHeight: '850px' }}>
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#FAFAF8] z-10">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-[#0D9488]/20 border-t-[#0D9488] rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-[#64748B] font-medium">
                        {menuLanguage === 'en' ? 'Loading menu...' : 'Cargando menú...'}
                      </p>
                    </div>
                  </div>
                )}
                <iframe
                  key={`${selectedLocation}-${menuLanguage}`}
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(window.location.origin + currentPdf)}&embedded=true`}
                  className="w-full h-full border-0"
                  title={`${currentMenu.name[menuLanguage]} Menu`}
                  onLoad={() => setIsLoading(false)}
                  loading="lazy"
                  allow="autoplay"
                />
              </div>
            </div>

            {/* Mobile-friendly alternative */}
            <div className="mt-8 text-center lg:hidden">
              <p className="text-[#64748B] text-sm mb-4">
                {menuLanguage === 'en' 
                  ? 'For the best experience on mobile, download or open the menu in a new tab.'
                  : 'Para una mejor experiencia en móvil, descarga o abre el menú en una nueva pestaña.'}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={`https://docs.google.com/viewer?url=${encodeURIComponent(window.location.origin + currentPdf)}&embedded=true`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#0D9488] text-white font-semibold rounded-xl hover:bg-[#0F766E] transition-colors w-full sm:w-auto justify-center"
                >
                  <ExternalLink className="w-5 h-5" />
                  {menuLanguage === 'en' ? 'View Full Menu' : 'Ver Menú Completo'}
                </a>
                <a
                  href={currentPdf}
                  download
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#0C4A6E] text-[#0C4A6E] font-semibold rounded-xl hover:bg-[#0C4A6E] hover:text-white transition-colors w-full sm:w-auto justify-center"
                >
                  <Download className="w-5 h-5" />
                  {menuLanguage === 'en' ? 'Download PDF' : 'Descargar PDF'}
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Location Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="max-w-3xl mx-auto text-center mt-10"
        >
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl px-6 py-4 border border-[#E2E8F0]">
            <p className="text-[#64748B] text-sm mb-2">
              {selectedLocation === 'main' ? (
                menuLanguage === 'en'
                  ? '📍 This menu is available at El Tezal and Centro Los Cabos locations.'
                  : '📍 Este menú está disponible en las ubicaciones de El Tezal y Centro Los Cabos.'
              ) : (
                menuLanguage === 'en'
                  ? '📍 This menu is exclusively available at our Carretera Transpeninsular location - authentic carreta style mariscos.'
                  : '📍 Este menú está disponible exclusivamente en nuestra ubicación de Carretera Transpeninsular - auténticos mariscos estilo carreta.'
              )}
            </p>
            <p className="text-[#94A3B8] text-xs">
              {menuLanguage === 'en' 
                ? 'Prices are in Mexican Pesos (MXN). Menu items and prices subject to change.'
                : 'Precios en Pesos Mexicanos (MXN). Los platillos y precios pueden cambiar.'}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
